import { Users, Cog, Fuel, Calendar, Gauge, Snowflake, PawPrint, Ban, CigaretteOff } from "lucide-react";
import { matchFeature } from "@/components/car/featureCatalog";

// Iter 67d: per-spec icon palette. A subtle 8x8 tinted square behind each lucide
// glyph reads as a "spec chip" rather than a row of grey icons, and matches the
// stepper / pill colour language used elsewhere.
const SPEC_TINT = {
  Seats:        { bg: "bg-sky-50",     fg: "text-sky-600",     ring: "ring-sky-100" },
  Transmission: { bg: "bg-indigo-50",  fg: "text-indigo-600",  ring: "ring-indigo-100" },
  Fuel:         { bg: "bg-amber-50",   fg: "text-amber-600",   ring: "ring-amber-100" },
  "A/C":        { bg: "bg-cyan-50",    fg: "text-cyan-600",    ring: "ring-cyan-100" },
  Pets:         { bg: "bg-emerald-50", fg: "text-emerald-600", ring: "ring-emerald-100" },
  "No pets":    { bg: "bg-neutral-50", fg: "text-neutral-500", ring: "ring-neutral-200" },
  Smoking:      { bg: "bg-amber-50",   fg: "text-amber-700",   ring: "ring-amber-200" },
  "No smoking": { bg: "bg-rose-50",    fg: "text-rose-600",    ring: "ring-rose-100" },
  Engine:       { bg: "bg-violet-50",  fg: "text-violet-600",  ring: "ring-violet-100" },
  Year:         { bg: "bg-rose-50",    fg: "text-rose-600",    ring: "ring-rose-100" },
};

function _hasAircon(car) {
  for (const f of (car?.features || [])) {
    const m = matchFeature(f);
    if (m && m.key === "ac") return true;
  }
  return false;
}

export default function CarSpecsRow({ car }) {
  // Iter 67bo — pet & smoking flags are explicit boolean fields with safe
  // defaults: missing field === conservative "no". The spec row always shows
  // the smoking chip (renters specifically search "smoking allowed"); pets is
  // shown only when explicitly allowed since "no pets" is the default.
  const petAllowed = !!car?.pet_friendly;
  const smokingAllowed = !!car?.smoking_allowed;

  const items = [
    { icon: Users, label: "Seats", val: car.seats },
    { icon: Cog, label: "Transmission", val: car.transmission },
    { icon: Fuel, label: "Fuel", val: car.fuel_type },
    _hasAircon(car) && { icon: Snowflake, label: "A/C", val: "Climate control", testId: "spec-aircon" },
    petAllowed && { icon: PawPrint, label: "Pets", val: "Welcome", testId: "spec-pets" },
    smokingAllowed
      ? { icon: Ban, label: "Smoking", val: "Permitted", testId: "spec-smoking-allowed" }
      : { icon: CigaretteOff, label: "No smoking", val: "Strict", testId: "spec-smoking-banned" },
    car.engine_size && { icon: Gauge, label: "Engine", val: car.engine_size, testId: "spec-engine-size" },
    { icon: Calendar, label: "Year", val: car.year },
  ].filter(Boolean);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-6 py-8 border-b border-border" data-testid="car-specs-row">
      {items.map((s) => {
        const tint = SPEC_TINT[s.label] || SPEC_TINT.Year;
        return (
          <div key={s.label} className="flex items-center gap-3" data-testid={s.testId}>
            <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${tint.bg} ring-1 ${tint.ring}`}>
              <s.icon className={`h-4.5 w-4.5 ${tint.fg}`} />
            </div>
            <div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider">{s.label}</div>
              <div className="text-sm font-medium">{s.val}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
